import React, { useCallback, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { cloneDeep } from "lodash";
import styles from "./CopyAgendaItemPreview.module.css";
import { AgendaItem } from "src/types";
import { toJS } from "mobx";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey, Role } from "src/network/User";
import { GetAgendaitemInformationFromView_agendaitemtemplatetexts } from "src/api/generated/GetAgendaitemInformationFromView";
import { MessageType } from "src/components/notifications/Notifier";
import Axios from "src/network/Axios";

interface CopyAssemblyAgendaModalProps {
    assemblyid: string;
    agendaitemTemplatesCounter: number;
    agendaItemTemplates: GetAgendaitemInformationFromView_agendaitemtemplatetexts[] | undefined;
    isAssemblyLoaded: boolean;
}

const CopyAgendaItemPreviewBase = (props: CopyAssemblyAgendaModalProps) => {
    const { t } = useTranslation();
    const { assemblyStore, authStore, uiStore, agendaitemStore } = useContext(RootStoreContext);
    const { assemblyid, agendaitemTemplatesCounter, agendaItemTemplates, isAssemblyLoaded } = props;

    useEffect(() => {
        agendaitemStore.updateNavigation(assemblyStore.currentAssemblyTitle);

        if (isAssemblyLoaded) {
            agendaitemStore.loadAgendaItemsToCopyFromAssembly(assemblyid);
        }
    }, [agendaitemStore, assemblyid, isAssemblyLoaded, assemblyStore.currentAssemblyTitle]);

    const printStatusMessage = useCallback(
        (message: string, msgType: MessageType) => {
            uiStore.enqueueSnackbar({
                content: message,
                options: {
                    type: msgType,
                    autoClose: msgType === MessageType.ERROR ? false : 3000
                }
            });
        },
        [uiStore]
    );

    const onClickApply = useCallback(async () => {
        if (assemblyStore.selectedCopyAssemblyAgendaOption > 0) {
            const unsavedAgendaItems: AgendaItem[] = [];
            if (assemblyStore.agendaItemsHaveChanged) {
                const tempNotSaveAgendaItems = cloneDeep(toJS(assemblyStore.agendaItems));
                tempNotSaveAgendaItems.forEach((agi) => {
                    if (agi.isNew) {
                        unsavedAgendaItems.push(agi);
                    }
                });
                if (unsavedAgendaItems) {
                    assemblyStore.setUnsavedAgendaItems(unsavedAgendaItems);
                }
            }

            if (authStore.user && authStore.token) {
                const copyUrl =
                    assemblyStore.selectedCopyAssemblyAgendaOption === 1
                        ? `${NetworkConfig.newCopyagendaitems}?newassemblyid=${assemblyid}&oldassemblyid=${agendaitemStore.selectedAssembly}`
                        : `${NetworkConfig.newCopyagendaitemTemplates}?newassemblyid=${assemblyid}`;

                try {
                    await Axios.getAuthInstance().get(copyUrl, {
                        urlWithoutParams:
                            assemblyStore.selectedCopyAssemblyAgendaOption === 1
                                ? NetworkConfig.newCopyagendaitems
                                : NetworkConfig.newCopyagendaitemTemplates,
                        headers: {
                            "x-hasura-role": getRoleKey(Role.MANAGER)
                        }
                    });

                    if (assemblyStore.selectedCopyAssemblyAgendaOption === 1) {
                        printStatusMessage(
                            t("screens.realestate.assembly.copy_agendaitem_modal.agendacopymessage"),
                            MessageType.INFO
                        );
                    } else {
                        printStatusMessage(
                            t("screens.realestate.assembly.copy_agendaitem_modal.agendatemplatecopymessage"),
                            MessageType.INFO
                        );
                    }

                    assemblyStore.setIsCopyAgendaItemsActive(true);
                    assemblyStore.generateAssemblyRealestatePDF(assemblyid);

                    // Reload the page
                    window.location.reload();
                } catch (error) {
                    // No-OP
                }
            }
        }
    }, [
        authStore.token,
        authStore.user,
        assemblyid,
        t,
        assemblyStore,
        agendaitemStore.selectedAssembly,
        printStatusMessage
    ]);

    const onRadioOptionChange = useCallback(
        (event: any) => {
            if (event.currentTarget.value === "AGENDAITEMS_LAST_ASSEMBLY") {
                assemblyStore.setSelectedCopyAssemblyAgendaOption(1);
            } else if (event.currentTarget.value === "AGENDAITEMS_TEMPLATE_ASSEMBLY") {
                assemblyStore.setSelectedCopyAssemblyAgendaOption(2);
            } else {
                assemblyStore.setSelectedCopyAssemblyAgendaOption(0);
            }
        },
        // eslint-disable-next-line
        [assemblyStore, assemblyStore.selectedCopyAssemblyAgendaOption]
    );

    const onSelectDropdown = useCallback(
        async (realestateId: string) => {
            const selected = assemblyStore.formattedRealestates.find((realestate) => realestate.id === realestateId);

            if (selected) {
                // Set the selected real estate
                agendaitemStore.setSelectedRealestate(selected);

                // Wait for loadAgendaItemsToCopy to complete
                await agendaitemStore.loadAgendaItemsToCopy(
                    selected.id,
                    assemblyid,
                    assemblyStore.currentAssembly?.date || ""
                );

                // After loading is complete, set the selected assembly
                agendaitemStore.setSelectedAssembly(
                    agendaitemStore.currentAgendaItemToCopy && agendaitemStore.currentAgendaItemToCopy.length > 0
                        ? agendaitemStore.currentAgendaItemToCopy[0].assemblyid
                        : ""
                );
            }

            // Set the copy assembly agenda option
            assemblyStore.setSelectedCopyAssemblyAgendaOption(1);
        },
        [assemblyStore, agendaitemStore, assemblyid]
    );

    const Content = () => {
        return (
            <>
                {assemblyStore.formattedRealestates && assemblyStore.formattedRealestates.length > 0 ? (
                    <UI.Row className={"mt-1"}>
                        <UI.Form.Check
                            key={"AGENDAITEMS_LAST_ASSEMBLY"}
                            id={t(
                                "screens.realestate.assembly.copy_agendaitem_modal.agendaitems_from_last_assembly"
                            ).toString()}
                            className={`${styles.RadioButton} d-flex align-items-center`}
                            label={t(
                                "screens.realestate.assembly.copy_agendaitem_modal.agendaitems_from_last_assembly"
                            )}
                            type={"radio"}
                            checked={assemblyStore.selectedCopyAssemblyAgendaOption === 1}
                            value={"AGENDAITEMS_LAST_ASSEMBLY"}
                            onChange={onRadioOptionChange}
                        />
                        <UI.Dropdown
                            id="sort-realestates-dorpdown"
                            onSelect={onSelectDropdown}
                            items={assemblyStore.formattedRealestates.map((item) => ({
                                label: item.name,
                                value: item.id
                            }))}
                            label={agendaitemStore.selectedRealestate?.name || ""}
                            defaultLabel={assemblyStore.formattedRealestates[0].name}
                            className={styles.RealestateDropdown}
                        />
                    </UI.Row>
                ) : undefined}

                {agendaitemTemplatesCounter > 0 ? (
                    <UI.Row className={"mt-1"}>
                        <UI.Form.Check
                            key={"AGENDAITEMS_TEMPLATE_ASSEMBLY"}
                            id={t(
                                "screens.realestate.assembly.copy_agendaitem_modal.agendaitems_from_template"
                            ).toString()}
                            className={`${styles.RadioButton} d-flex align-items-center`}
                            label={t("screens.realestate.assembly.copy_agendaitem_modal.agendaitems_from_template")}
                            type={"radio"}
                            checked={assemblyStore.selectedCopyAssemblyAgendaOption === 2}
                            value={"AGENDAITEMS_TEMPLATE_ASSEMBLY"}
                            onChange={onRadioOptionChange}
                        />
                    </UI.Row>
                ) : undefined}

                {agendaitemStore.currentAgendaItemToCopy &&
                agendaitemStore.currentAgendaItemToCopy.length > 0 &&
                assemblyStore.selectedCopyAssemblyAgendaOption === 1 ? (
                    <UI.Row className={"mt-3"}>
                        <ul className={styles.AgendaList}>
                            {agendaitemStore.currentAgendaItemToCopy?.map((parentitem) => (
                                <li key={parentitem.sort + parentitem.id}>
                                    <div
                                        className={styles.ParentAgendaItems}
                                    >{`${parentitem.sort}. ${parentitem.text}`}</div>
                                    {parentitem.subagendaitemtext.map((childItem) => (
                                        <div className={styles.ChildAgendaItems} key={childItem.sort + childItem.id}>
                                            {`${parentitem.sort}.${childItem.sort} ${childItem.text}`}
                                        </div>
                                    ))}
                                </li>
                            ))}
                        </ul>
                    </UI.Row>
                ) : undefined}

                {agendaitemTemplatesCounter > 0 && assemblyStore.selectedCopyAssemblyAgendaOption === 2 ? (
                    <UI.Row className={"mt-3"}>
                        <ul className={styles.AgendaList}>
                            {agendaItemTemplates?.map((parentitem) => (
                                <li key={parentitem.sort + parentitem.id}>
                                    <div
                                        className={styles.ParentAgendaItems}
                                    >{`${parentitem.sort}. ${parentitem.text}`}</div>
                                    {parentitem.subagendaitemtemplatetext.map((childItem) => (
                                        <div className={styles.ChildAgendaItems} key={childItem.sort + childItem.id}>
                                            {`${parentitem.sort}.${childItem.sort} ${childItem.text}`}
                                        </div>
                                    ))}
                                </li>
                            ))}
                        </ul>
                    </UI.Row>
                ) : undefined}
            </>
        );
    };

    let agendaItemsExists: boolean = true;

    if (assemblyStore.selectedCopyAssemblyAgendaOption === 1) {
        agendaItemsExists = (agendaitemStore.currentAgendaItemToCopy?.length ?? 0) > 0;
    }

    return (
        <UI.Container className="pt-4">
            <UI.Row>
                <UI.Col xs={10} sm={10} md={10}>
                    <UI.Title>{t("screens.assemblies.agendas")}</UI.Title>
                </UI.Col>
                <UI.Col xs={2} sm={2} md={2}>
                    <UI.Button label={t("labels.apply")} onClick={onClickApply} disabled={!agendaItemsExists} />
                </UI.Col>
            </UI.Row>
            <UI.Row>
                <UI.Col className={styles.ColContent}>
                    <Content />
                </UI.Col>
            </UI.Row>
        </UI.Container>
    );
};

export const CopyAgendaItemPreview = Sentry.withProfiler(observer(CopyAgendaItemPreviewBase));
